import React, { useState, useCallback, useLayoutEffect, useRef } from 'react';
import { useImageLoader } from '../hooks/useImageLoader';
import PhotoMeta from './photo/PhotoMeta';

const MOVE_INTO_BAR = { duration: 720, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'backwards' };

function HomePage() {
  // Flips once, on the first press: the hero becomes the top bar and stays there.
  const [engaged, setEngaged] = useState(false);
  const [photoInfo, setPhotoInfo] = useState(null);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [infoFailed, setInfoFailed] = useState(false);

  const {
    progress,
    isLoading,
    error: imageError,
    blobUrl: currentPhoto,
    loadImage,
    reset
  } = useImageLoader(null, { autoLoad: false });

  const busy = fetchingInfo || isLoading;

  const loadRandomPhoto = useCallback(async () => {
    reset();
    setFetchingInfo(true);
    setInfoFailed(false);

    try {
      const response = await fetch('/api/random-photo-info');
      if (!response.ok) throw new Error(`Photo info returned ${response.status}`);

      const info = await response.json();
      setPhotoInfo(info);
      // The 4k display copy, not the original: this only ever fills the screen,
      // so the remaining ~40 MB of an original buys nothing you can see. There is
      // deliberately no cache-buster on it either, so a repeat doesn't re-download.
      loadImage(info.displayUrl || info.url);
    } catch (err) {
      console.error('Error loading photo:', err);
      setInfoFailed(true);
    } finally {
      setFetchingInfo(false);
    }
  }, [loadImage, reset]);

  // FLIP: the CSS decides where the name, tagline and button finish up, at any
  // screen size. All this does is measure where each one was, then animate it from
  // there to wherever it now is. Nothing here knows about left, top or right, which
  // is what keeps the desktop and phone layouts from each needing their own
  // choreography. Only `transform` animates, so it stays smooth on a phone.
  const pieces = useRef([]);
  const before = useRef(null);

  const handlePress = () => {
    if (!engaged) {
      before.current = pieces.current.map((el) => ({
        rect: el.getBoundingClientRect(),
        fontSize: parseFloat(getComputedStyle(el).fontSize)
      }));
      setEngaged(true);
    }
    loadRandomPhoto();
  };

  useLayoutEffect(() => {
    const from = before.current;
    if (!from) return;
    before.current = null;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    pieces.current.forEach((el, index) => {
      const to = el.getBoundingClientRect();
      const dx = from[index].rect.left + from[index].rect.width / 2 - (to.left + to.width / 2);
      const dy = from[index].rect.top + from[index].rect.height / 2 - (to.top + to.height / 2);
      // From font sizes rather than widths: the tagline can wrap in the hero but
      // not in the bar, and a width ratio would stretch it on the way there.
      const scale = from[index].fontSize / parseFloat(getComputedStyle(el).fontSize);

      el.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
          { transform: 'none' }
        ],
        { ...MOVE_INTO_BAR, delay: index * 50 }
      );
    });
  }, [engaged]);

  const failed = infoFailed || imageError;

  return (
    <div className={`home${engaged ? ' engaged' : ''}`}>
      <header className="home-head">
        <h1 className="home-name" ref={(el) => { pieces.current[0] = el; }}>
          Keaton Dalquist
        </h1>
        <p className="home-tagline" ref={(el) => { pieces.current[1] = el; }}>
          I am a software engineer who also enjoys photography.
        </p>
        <button
          type="button"
          className="photo-button"
          ref={(el) => { pieces.current[2] = el; }}
          onClick={handlePress}
          disabled={busy}
        >
          <span className="label-full">Load Random Photo</span>
          <span className="label-short">Random Photo</span>
        </button>
      </header>

      {engaged && (
        <div className="home-view">
          <div className="home-stage">
            {busy && (
              <div className="home-loading-container">
                <div className="home-progress-bar">
                  <div className="home-progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <span className="home-progress-text">Loading {progress}%</span>
              </div>
            )}
            {failed && !busy && (
              <p className="home-error">That photo didn't load. Try another.</p>
            )}
            {currentPhoto && !busy && (
              <img
                src={currentPhoto}
                alt={photoInfo ? `${photoInfo.place} - ${photoInfo.filename}` : ''}
                className="random-photo"
              />
            )}
          </div>

          {/* Stays up between photos rather than vanishing during the next load,
              so the stage above doesn't change size and shove the picture around. */}
          {photoInfo && (
            <PhotoMeta
              place={photoInfo.place}
              placeTo={`/photography/${encodeURIComponent(photoInfo.place)}`}
              filename={photoInfo.filename}
              exif={photoInfo.exif}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default HomePage;
